import {
  Bank,
  MapPin,
  Sneaker,
  Target,
  Barbell,
  Basket,
  Basketball,
  BeerStein,
  Binoculars,
  Books,
  Boot,
  Bread,
  CastleTurret,
  Coffee,
  FilmSlate,
  Fish,
  FlagCheckered,
  ForkKnife,
  Golf,
  MaskHappy,
  Martini,
  Mountains,
  MusicNotes,
  PawPrint,
  Planet,
  Popcorn,
  Sailboat,
  ShoppingBag,
  Snowflake,
  SoccerBall,
  SwimmingPool,
  TennisBall,
  Tree,
  UmbrellaSimple,
  Volleyball,
  Wine,
  type Icon,
} from '@phosphor-icons/react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { SANS_GENRE, type FamilleDeLieu, LieuTrouve } from './recherche';

/**
 * Le repere d'un lieu : une pastille a sa couleur, et une icone qui dit ce que c'est.
 *
 * Elle a remplace la punaise le 1er novembre 2026 : l'aiguille barrait la photo, et il
 * fallait connaitre la legende des couleurs pour savoir ce qu'on regardait.
 */

const ICONES: Record<string, Icon> = {
  Restaurant: ForkKnife,
  Cafe: Coffee,
  Bakery: Bread,
  FoodMarket: Basket,
  Nightlife: Martini,
  Brewery: BeerStein,
  Winery: Wine,
  Distillery: Wine,
  MovieTheater: FilmSlate,
  Theater: MaskHappy,
  MusicVenue: MusicNotes,
  Bowling: Target,
  MiniGolf: Golf,
  GoKart: FlagCheckered,
  AmusementPark: Popcorn,
  Stadium: SoccerBall,
  Soccer: SoccerBall,
  Tennis: TennisBall,
  Basketball,
  Swimming: SwimmingPool,
  Volleyball,
  Golf,
  Skating: Snowflake,
  RockClimbing: Mountains,
  SkatePark: Sneaker,
  FitnessCenter: Barbell,
  Museum: Bank,
  Landmark: Binoculars,
  Castle: CastleTurret,
  Fortress: CastleTurret,
  NationalMonument: Bank,
  Library: Books,
  Planetarium: Planet,
  Aquarium: Fish,
  Zoo: PawPrint,
  Park: Tree,
  Beach: UmbrellaSimple,
  Hiking: Boot,
  NationalPark: Mountains,
  Marina: Sailboat,
  Store: ShoppingBag,
  [SANS_GENRE]: MapPin,
};

/* Un lieu qu'Apple n'a pas classe prend l'icone de sa famille. */
const ICONES_DE_FAMILLE: Record<FamilleDeLieu, Icon> = {
  restos: ForkKnife,
  sorties: Martini,
  sport: SoccerBall,
  culture: Bank,
  nature: Tree,
  commerces: ShoppingBag,
};

/*
  Le dessin de chaque icone, rendu une fois puis recopie.

  MapKit veut un element tout fait, pas un composant, et monter React pour chaque repere
  pesait pour rien : quelques formes, toujours les memes. Tout se prepare d'avance, hors
  de tout rendu : appele pendant un effet, `flushSync` ne fait rien, et des pastilles
  sortaient sans icone.
*/
const dessins = new Map<Icon, string>();

export function preparerLesIcones(): void {
  if (dessins.size > 0) return;
  for (const Dessin of new Set([...Object.values(ICONES), ...Object.values(ICONES_DE_FAMILLE)])) {
    const hote = document.createElement('div');
    const racine = createRoot(hote);
    flushSync(() => racine.render(<Dessin size={18} aria-hidden="true" />));
    dessins.set(Dessin, hote.innerHTML);
    racine.unmount();
  }
}

/* Celle dont la fiche est ouverte : retenue, car les reperes vont et viennent. */
let levee: string | null = null;

export function leverLaPastille(id: string | null): void {
  levee = id;
  document.querySelectorAll<HTMLElement>('.lx-pastille').forEach((pastille) => {
    pastille.classList.toggle('lx-pastille--levee', id !== null && pastille.dataset.lieu === id);
  });
}

export function pastilleDeLieu(lieu: LieuTrouve, couleur: string, onChoisir: () => void): HTMLButtonElement {
  const pastille = document.createElement('button');
  pastille.type = 'button';
  pastille.dataset.lieu = lieu.id;
  pastille.className = 'lx-pastille';
  if (lieu.id === levee) pastille.classList.add('lx-pastille--levee');
  pastille.style.setProperty('--cat', couleur);
  pastille.setAttribute('aria-label', lieu.nom);
  const icone = (lieu.categorie && ICONES[lieu.categorie]) || ICONES_DE_FAMILLE[lieu.famille];
  pastille.innerHTML = dessins.get(icone) ?? '';
  pastille.addEventListener('click', (clic) => {
    /* Le fond prendrait le clic pour un appui a cote. */
    clic.stopPropagation();
    leverLaPastille(lieu.id);
    onChoisir();
  });
  return pastille;
}
